
import { Component, Watch } from 'vue-property-decorator'

import { Mixins } from 'vue-mixin-decorator'
import MixinInterface from '@/_mixins/index'
import UserMixin from '@/_mixins/user'

import ImpersonateBanner from '@/_components/banner/impersonate/impersonate.vue'
import VerifyBanner from '@/_components/banner/verify/verify.vue'
import MissingInfoBanner from './_components/banner/missingInfo.vue'

import Snackbar from '@/components/Snackbar/Snackbar.vue'
import Menu from '@/components/Menu/Menu/Menu.vue'

import ToolbarComponent from '@/_components/bar/appToolbar/appToolbar.vue'

import Authentication from '@/views/Authentication/models/authentication'
import AuthorizationMixin from './_mixins/authorization'
import { name } from '../package.json'
import LanguageModel from '@/views/Language/models/language'
import { searchCompanyAdminsByUser } from '@/views/Authorization/utils/authorization'
import { isApolloError } from '@apollo/client/core'
import NewUpdateMixin from '@/_mixins/newUpdate'

@Component({
    mixins: [UserMixin, AuthorizationMixin, NewUpdateMixin],
    components: {
        ImpersonateBanner,
        VerifyBanner,
        MissingInfoBanner,
        Menu,
        Snackbar,
        ToolbarComponent,
    },
})
export default class App extends Mixins<MixinInterface>(UserMixin, AuthorizationMixin) {
    get isAppReady() {
        return Object.keys(this.$i18n.messages).length && !this.loading
    }

    get isSafari() {
        return (
            navigator.userAgent.indexOf('Safari') != -1 &&
            navigator.userAgent.indexOf('Chrome') == -1
        )
    }

    reload() {
        window.location.reload()
    }

    updateDocumentTitle() {
        let title

        if (this.$route.meta?.title) {
            title = this.$t(this.$route.meta?.title) as string
        }

        if (!this.isProject && !this.isGroup) {
            document.title = title ? `${title} | ${name}` : name
            return
        }

        let projectOrGroupName =
            this.currentGroup?.attributes?.name || this.currentProject?.attributes?.name

        if (title) {
            document.title = projectOrGroupName ? `${title} | ${projectOrGroupName}` : title
        } else {
            document.title = projectOrGroupName || name
        }
    }

    @Watch('isAuthenticated')
    async handleUnauthenticated() {
        this.loading = true

        const topLevelRoute = this.$route.matched[0]?.name

        // If isAuthenticated changed to true:
        // 1. We hide menu on the smaller screen, since menu is only available when user is authenticate
        // 2. We redirect user to the url he was trying to access before being authenticated and if it's missing redirect him to the user view
        if (this.isAuthenticated) {
            await this.$store.commit('toggleMobileDrawerState', false)
            await this.$router.push(
                this.$route.query.url ? `${this.$route.query.url}` : { name: 'user' }
            )
        } else {
            // If isAuthenticated changed to false and user is not on the authentication page or the page that does not require authentication, we redirect him to the authentication page
            const notRequiredAuth =
                this.$route.matched.findIndex((item) => item.meta['notRequiredAuth']) > -1

            if (
                this.$route.fullPath !== '/' &&
                topLevelRoute !== 'authentication' &&
                !notRequiredAuth
            ) {
                await this.$router.push({
                    name: 'sign-in',
                    query: { url: this.$route.fullPath },
                })
            }
        }
        this.loading = false
    }

    @Watch('userLanguage', { immediate: true })
    async setLanguage() {
        if (this.userLanguage) {
            let userLangs = [this.userLanguage]
            if (process.env.NODE_ENV === 'production' && this.userLanguage !== 'eng') {
                userLangs.push('eng')
            }
            await LanguageModel.getFinalStrings(userLangs)

            this.$i18n.locale = this.userLanguage

            this.$i18n.setLocaleMessage(this.userLanguage, this.$store.getters.effectiveTexts)

            this.$watch('isProject', this.updateDocumentTitle)
            this.$watch('isGroup', this.updateDocumentTitle)
            this.$watch('$route', this.updateDocumentTitle, { immediate: true })
        } else {
            this.$store.commit('userLanguage', this.setSystemLanguage())
        }
    }

    @Watch('currentUser')
    handleUser() {
        if (!this.currentUser && !this.updateExists) {
            this.$store.commit('authenticated', false)
        }
    }

    key = 0

    @Watch('impersonated')
    @Watch('isAuthenticated')
    async updateView() {
        if (this.isAuthenticated && this.currentUser) {
            try {
                this.loading = true
                this.key += 1
                await Promise.all([
                    Authentication.getBklrProjects(),
                    searchCompanyAdminsByUser(this.currentUser.id, true),
                ])
            } finally {
                this.loading = false
            }
        }
    }

    loading = true

    @Watch('isAppReady')
    calculateAppHeight() {
        if (!this.isAppReady || !this.isSafari) return
        document.body.style.setProperty('--app-height', `${window.innerHeight}px`)
    }

    // TURN href from translation strings into router-link. Avoid reloading page
    async mounted() {
        try {
            this.loading = true

            await Promise.all([Authentication.getCurrentSession(), LanguageModel.getAllLanguages()])

            if (this.isAuthenticated) {
                await Promise.all([
                    Authentication.getBklrProjects(),
                    searchCompanyAdminsByUser(this.currentUser.id, true),
                ])
            } else {
                await this.handleUnauthenticated()
            }
        } finally {
            this.loading = false
        }

        if (this.isSafari) {
            window.addEventListener('resize', this.calculateAppHeight)
        }

        window.addEventListener('click', (event) => {
            // ensure we use the link, in case the click has been received by a subelement
            let { target }: any = event
            while (target && target.tagName !== 'A') target = target.parentNode
            // handle only links that do not reference external, malto and tell resources
            if (
                target &&
                target.matches("a:not([href*='://'])") &&
                target.href &&
                !target.href.includes('mailto') &&
                !target.href.includes('tel')
            ) {
                // some sanity checks taken from vue-router:
                // https://github.com/vuejs/vue-router/blob/dev/src/components/link.js#L106
                const { altKey, ctrlKey, metaKey, shiftKey, button, defaultPrevented } = event
                // don't handle with control keys
                if (metaKey || altKey || ctrlKey || shiftKey) return
                // don't handle when preventDefault called
                if (defaultPrevented) return
                // don't handle right clicks
                if (button !== undefined && button !== 0) return
                // don't handle if `target="_blank"`
                if (target && target.getAttribute) {
                    const linkTarget = target.getAttribute('target')
                    if (/\b_blank\b/i.test(linkTarget)) return
                }
                // don't handle same page links/anchors
                const url = new URL(target.href)
                const to = url.pathname
                if (window.location.pathname !== to && event.preventDefault) {
                    event.preventDefault()
                    this.$router.push(to)
                }
            }
        })
    }

    errorCaptured(error) {
        if (isApolloError(error)) {
            this.$store.commit('setErrorSnackBar', {
                url: `${error}`,
            })
        }
    }
}
